/**
 * Which expiry warnings are still owed, for one deadline.
 *
 * Pulled out of useAdminSessionWatcher because the interesting cases are all
 * arithmetic and none of them are reachable from a UI test: a tab that slept
 * through two rungs, a session extended from inside a warning, and a session
 * pinned at its 12-hour ceiling where extending cannot clear the warning band.
 */

/**
 * The rungs still ahead of us when a deadline is established.
 *
 * Rungs already behind the deadline are dropped, not queued. A session extended
 * to four minutes because it hit its absolute ceiling has passed all three
 * rungs already; queueing them would fire a warning the instant the member
 * pressed the button that was meant to dismiss one.
 *
 * @param {Number[]} thresholds the ladder, in any order
 * @param {Number} remainingSeconds life left at the moment the deadline arrived
 * @returns {Number[]} the rungs still to fire, largest first
 */
export const armWarningThresholds = (
    thresholds: number[],
    remainingSeconds: number
): number[] =>
    [...(thresholds || [])]
        .sort((a, b) => b - a)
        .filter(threshold => threshold < remainingSeconds);

/**
 * The rung to show now, if the clock has crossed one.
 *
 * The *innermost* rung crossed, not the outermost owed. Those are the same
 * thing while the tab is awake and the rungs arrive one at a time, and quite
 * different after a sleep: a tab that goes quiet at 25 minutes and wakes at 4
 * has crossed all three, and the one to show is "5 minutes", because "20
 * minutes left" would simply be untrue.
 *
 * `pending` must be largest-first, as armWarningThresholds returns it, so the
 * last crossed entry is the smallest.
 *
 * @param {Number[]} pending rungs not yet fired
 * @param {Number} remainingSeconds life left now
 * @returns {Number|undefined} the rung to show, or undefined if none is due
 */
export const dueWarningThreshold = (
    pending: number[],
    remainingSeconds: number
): number | undefined => {
    const crossed = (pending || []).filter(threshold => threshold >= remainingSeconds);

    return crossed.length ? crossed[crossed.length - 1] : undefined;
};

/**
 * What remains owed once the crossed rung has been shown.
 *
 * Every rung at or above the current time is dropped, not just the first. A tab
 * asleep from 25 minutes to 4 minutes wakes past all three, and showing "20
 * minutes left" at that point would simply be false — the member should see the
 * innermost rung they have actually reached, once.
 *
 * @param {Number[]} pending rungs not yet fired
 * @param {Number} remainingSeconds life left now
 * @returns {Number[]} the rungs still to fire
 */
export const consumeWarningThresholds = (
    pending: number[],
    remainingSeconds: number
): number[] => (pending || []).filter(threshold => threshold < remainingSeconds);
